import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import AHDate from "_shared/dates/date";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import RowActions from "_shared/_views/tables/row-actions";
import SearchMatch, { SearchMatchScope } from "_shared/_views/tables/search-match";
import PermissionTemplateFilterParameters from "permissions/_api/queries/filtering/permission-template-filter-parameters";
import PermissionTemplateOrderParameters from "permissions/_api/queries/ordering/permission-template-order-parameters";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import {
  useDeletePermissionTemplateByIdMutation,
  useGetPermissionTemplatesQuery,
} from "permissions/_api/permission-templates-api-service";
import PermissionsSubjectHeader from "permissions/_views/scaffold/permissions-subject-header";
import { personNameState, usePersonNames } from "permissions/_views/targets/person-name-states";
import { madeBy } from "permissions/_views/templates/template-maker";
import { NEW_TEMPLATE_PATH, templatePath } from "permissions/_views/templates/templates-paths";
import useSessionUser from "permissions/session/use-session-user";
import PermissionTemplate from "permissions/templates/permission-template";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";

/**
 * The organization's permission templates, at `/permissions/templates`: each one's name, what it
 * holds, who made it and when, and a menu to edit or delete it. Taken out of the matrix's
 * manage-templates mode, which the matrix's removal takes with it (#6177).
 *
 * A template belongs to the organization (#6826): `GET /permissions/templates` answers every
 * template of the caller's organization to an administrator with Member Permissions, entity-wide and
 * read/write, and refuses anyone else. Its maker is named, and has no more rights over it than the
 * other administrators.
 */

const PAGE = PaginationParameters.create({ pageIndex: 0, pageSize: 100 });

function grantsIn(template: PermissionTemplate): string {
  const granted = template.resources.filter((resource) => !resource.isDisabled).length;
  const takenAway = template.resources.length - granted;
  const parts = [`${granted} granted`];
  if (takenAway > 0) parts.push(`${takenAway} taken away`);
  return parts.join(" · ");
}

/** What the header says about whose templates these are. */
export const TEMPLATES_OWNERSHIP =
  "Your organization's templates. Every administrator who manages member permissions can apply, change or delete them, and applying one grants as you.";

/** Long enough that typing a name sends one request, not one per keystroke. */
const SEARCH_DEBOUNCE_MILLISECONDS = 300;

export default function TemplatesPage() {
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const [search, setSearch] = useState("");
  const [searchedName, setSearchedName] = useState("");
  const [deleting, setDeleting] = useState<PermissionTemplate>();
  const [deleteTemplate, deletion] = useDeletePermissionTemplateByIdMutation();

  // Memoized on purpose: the query is a class instance that reaches RTK Query's arguments.
  const query = useMemo(
    () =>
      new PermissionTemplateQuery({
        filterParameters: new PermissionTemplateFilterParameters({ name: searchedName }),
        orderParameters: new PermissionTemplateOrderParameters({}),
        paginationParameters: PAGE,
      }),
    [searchedName],
  );

  useEffect(() => {
    const timer = setTimeout(() => setSearchedName(search.trim()), SEARCH_DEBOUNCE_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [search]);
  const templatesQuery = useGetPermissionTemplatesQuery({ query });
  const templates = useMemo(
    () => PermissionTemplate.fromJSONArray(templatesQuery.data?.data ?? []),
    [templatesQuery.data],
  );
  // Memoized on purpose: a fresh set each render would be a fresh lookup argument.
  const makerIds = useMemo(() => new Set(templates.map((template) => template.userId.value)), [templates]);
  const makers = usePersonNames(makerIds);
  const today = AHDate.TODAY;

  if (!sessionUser) return null;
  if (!sessionUser.canManagePermissionTemplates()) return <NotAuthorized />;

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteTemplate({ id: deleting.id.value, version: deleting.version }).unwrap();
      enqueueSnackbar(`Deleted ${deleting.name}`, { variant: "success", role: "status" });
    } catch (error) {
      enqueueSnackbar(getErrorMessageFromUnknown(error) ?? "The template could not be deleted.", { variant: "error" });
    }
    setDeleting(undefined);
  };

  let list;
  if (templatesQuery.error) {
    list = (
      <Alert severity="error">{`Unable to load the templates. ${getErrorMessageFromUnknown(templatesQuery.error) ?? ""}`}</Alert>
    );
  } else if (templatesQuery.isLoading) {
    list = (
      <Box aria-busy="true" sx={{ display: "grid", gap: 1 }}>
        <Skeleton variant="rectangular" height={56} />
        <Skeleton variant="rectangular" height={56} />
      </Box>
    );
  } else if (templates.length === 0) {
    list = (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {searchedName ? `No template's name matches "${searchedName}".` : "Your organization has no templates yet."}
      </Typography>
    );
  } else {
    // The server searches a template's name, so its name marks what was searched; a description
    // is not searched and marks nothing (#6590).
    list = (
      <SearchMatchScope term={searchedName}>
        <Box
          component="ul"
          aria-label="Templates"
          sx={{ listStyle: "none", m: 0, p: 0, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}
        >
          {templates.map((template) => (
            <Box
              component="li"
              key={template.id.value}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-child": { borderBottom: 0 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Link component={RouterLink} to={templatePath(template.id.value)} sx={{ fontWeight: 500 }}>
                  <SearchMatch text={template.name} />
                </Link>
                {template.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                    {template.description}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" component="p">
                  {madeBy(personNameState(makers, template.userId.value), template.createdDate, today)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                {grantsIn(template)}
              </Typography>
              <RowActions
                collapsed
                ariaLabel={`Actions for ${template.name}`}
                actions={[
                  {
                    key: "edit",
                    label: "Edit template",
                    icon: <EditIcon />,
                    onClick: () => navigate(templatePath(template.id.value)),
                  },
                  {
                    key: "delete",
                    label: "Delete template",
                    icon: <DeleteOutlineIcon />,
                    destructive: true,
                    onClick: () => setDeleting(template),
                  },
                ]}
              />
            </Box>
          ))}
        </Box>
      </SearchMatchScope>
    );
  }

  return (
    <Box sx={{ height: "100%", minHeight: 0, overflowY: "auto" }}>
      <Box sx={{ maxWidth: 1100, mx: "auto", p: { xs: 2, md: 3 }, display: "grid", gap: 2 }}>
        <PermissionsSubjectHeader
          trail={[{ label: "Permissions", to: "/permissions" }]}
          title="Templates"
          avatar={<AutoFixHighIcon color="action" sx={{ fontSize: 40 }} />}
          secondary={TEMPLATES_OWNERSHIP}
          actions={
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(NEW_TEMPLATE_PATH)}>
              New template
            </Button>
          }
        />
        <TextField
          size="small"
          label="Find a template"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        {list}
      </Box>
      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(undefined)} aria-labelledby="delete-template-title">
        <DialogTitle id="delete-template-title">Delete template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Delete ${deleting?.name ?? "this template"}? Grants it has already made are not changed.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(undefined)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={deletion.isLoading} onClick={() => void confirmDelete()}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

